import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { CaseItem } from '@/types/content';
import { getPerson } from '@/utils/content';
import RelationBoard from '@/components/base/RelationBoard';
import SectionHeading from '@/components/base/SectionHeading';

interface ChartPreviewProps {
  item: CaseItem;
  defaultCenterId: string;
}

export default function ChartPreview({ item, defaultCenterId }: ChartPreviewProps) {
  const [centerId, setCenterId] = useState(defaultCenterId);
  const center = getPerson(centerId);

  return (
    <section className="w-full bg-background-100 border-y border-background-200">
      <div className="w-full px-4 md:px-8 py-14 md:py-20">
        <SectionHeading
          eyebrow="人物关系图"
          title="把人物关系摊开来看"
          desc="以任意一位当事人为中心，就能看到他与其他人的连线与关系标注。点击人物头像即可切换视角，快速捋清谁和谁有过交集。"
          action={
            <Link
              to="/charts"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-md border border-foreground-300 text-foreground-800 text-sm font-semibold hover:bg-background-200 transition-colors whitespace-nowrap"
            >
              <i className="ri-git-branch-line" />
              进入关系图总览
            </Link>
          }
        />

        <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-8">
            <RelationBoard
              centerId={centerId}
              relations={item.relations}
              onSelect={(id) => setCenterId(id)}
            />
          </div>
          <div className="lg:col-span-4 bg-background-50 border border-background-200 rounded-lg p-5">
            <p className="text-xs font-semibold tracking-[0.18em] text-primary-600 uppercase">当前中心</p>
            {center && (
              <div className="mt-4 flex items-center gap-3">
                <img
                  src={center.avatar}
                  alt={`${center.name} 头像`}
                  title={`${center.name} 关系中心 - 911吃瓜`}
                  className="w-14 h-14 rounded-full object-cover object-top"
                />
                <div>
                  <p className="font-heading font-bold text-foreground-950">{center.name}</p>
                  <p className="text-xs text-foreground-500">{center.role}</p>
                </div>
              </div>
            )}
            <p className="mt-4 text-sm leading-7 text-foreground-600">{center?.note}</p>

            <div className="mt-5 pt-5 border-t border-background-200">
              <p className="text-sm font-semibold text-foreground-900 mb-3">该事件关系条目</p>
              <ul className="space-y-2.5">
                {item.relations.map((rel, index) => {
                  const from = getPerson(rel.from);
                  const to = getPerson(rel.to);
                  return (
                    <li key={index} className="flex items-center gap-2 text-sm text-foreground-700">
                      <span className="font-medium whitespace-nowrap">{from?.name}</span>
                      <span className="flex-1 border-t border-dashed border-background-300" />
                      <span className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-[11px] whitespace-nowrap">
                        {rel.label}
                      </span>
                      <span className="flex-1 border-t border-dashed border-background-300" />
                      <span className="font-medium whitespace-nowrap">{to?.name}</span>
                    </li>
                  );
                })}
              </ul>
            </div>

            <Link
              to={`/case/${item.id}`}
              className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              查看该事件完整梳理
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}